import React, { useEffect, useRef, useState } from 'react';
import Slider from "react-slick";
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Custom arrow components
const PrevArrow = (props) => {
  const { onClick } = props;
  return (
    <button
      onClick={onClick}
      className="absolute left-4 top-1/2 z-10 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110"
    >
      <ChevronLeft className="w-6 h-6 text-sky-600" />
    </button>
  );
};

const NextArrow = (props) => {
  const { onClick } = props;
  return (
    <button
      onClick={onClick}
      className="absolute right-4 top-1/2 z-10 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110"
    >
      <ChevronRight className="w-6 h-6 text-sky-600" />
    </button>
  );
};

const AboutSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.2
      }
    );
  
    // 保存当前的 ref 值
    const currentRef = sectionRef.current;
  
    if (currentRef) {
      observer.observe(currentRef);
    }
  
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  // Slider settings
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    adaptiveHeight: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          dots: true,
        }
      },
      {
        breakpoint: 640,
        settings: {
          dots: true,
          arrows: false
        }
      }
    ],
    customPaging: (i) => (
      <div
        className={`w-2 h-2 rounded-full bg-white/60 hover:bg-white/80 transition-all duration-300
          ${i === 0 ? 'w-6 bg-white' : ''}`}
      />
    )
  };

  return (
    <section ref={sectionRef} className="min-h-screen bg-gradient-to-br from-white to-sky-50 relative">
      {/* Background Grid */}
      <div className="absolute inset-0" 
        style={{
          backgroundImage: 'linear-gradient(to right, #f1f5f9 1px, transparent 1px), linear-gradient(to bottom, #f1f5f9 1px, transparent 1px)',
          backgroundSize: '4rem 4rem'
        }}
      />
      
      <div className="max-w-7xl mx-auto px-4 py-24 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          {/* Left side - Image Slider */}
          <div className={`lg:w-1/2 w-full transform transition-all duration-1000 ${
            isVisible ? 'translate-x-0 opacity-100' : '-translate-x-16 opacity-0'
          }`}>
            <div className="relative group rounded-xl overflow-hidden shadow-2xl">
              <Slider {...sliderSettings} className="slick-custom">
                <div className="relative aspect-[4/3]">
                  <img 
                    src="/image2.jpg" 
                    alt="Profile 1"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="relative aspect-[4/3]">
                  <img 
                    src="/image1.jpg" 
                    alt="Profile 2"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="relative aspect-[4/3]">
                  <img 
                    src="/image3.jpg" 
                    alt="Profile 3"
                    className="w-full h-full object-cover"
                  />
                </div>
              </Slider>
            </div>
          </div>

          {/* Right side - Content */}
          <div className="lg:w-1/2 space-y-8">
            <div className={`transform transition-all duration-1000 delay-300 ${
              isVisible ? 'translate-x-0 opacity-100' : 'translate-x-16 opacity-0'
            }`}>
              <h2 className="text-4xl font-light text-slate-700 tracking-wide">
                About Me
              </h2>
            </div>
            
            <div className={`transform transition-all duration-1000 delay-500 ${
              isVisible ? 'translate-x-0 opacity-100' : 'translate-x-16 opacity-0'
            }`}>
              <p className="text-lg text-slate-600 leading-relaxed">
                I transform business challenges into intuitive digital solutions. With a journey from 
                Shanghai to Korea, and now Sydney, I bring a global perspective to every project.
              </p>
            </div>
            
            <div className={`transform transition-all duration-1000 delay-700 ${
              isVisible ? 'translate-x-0 opacity-100' : 'translate-x-16 opacity-0'
            }`}>
              <p className="text-lg text-slate-600 leading-relaxed">
                As a Mensa member and tech enthusiast, I thrive on solving complex problems with 
                elegant solutions.
              </p>
            </div>

            <div className={`space-y-6 pt-4 transform transition-all duration-1000 delay-900 ${
              isVisible ? 'translate-x-0 opacity-100' : 'translate-x-16 opacity-0'
            }`}>
              <div className="flex items-center space-x-4 p-4 rounded-lg bg-white/50 backdrop-blur-sm transition-all duration-300 hover:bg-white/80">
                <div className="text-sm font-medium text-sky-600">Location</div>
                <div className="text-slate-700">Sydney, Australia</div>
              </div>
              
              <div className="flex items-center space-x-4 p-4 rounded-lg bg-white/50 backdrop-blur-sm transition-all duration-300 hover:bg-white/80">
                <div className="text-sm font-medium text-sky-600">Experience</div>
                <div className="text-slate-700">5+ Years</div>
              </div>
              
              <div className="flex items-center space-x-4 p-4 rounded-lg bg-white/50 backdrop-blur-sm transition-all duration-300 hover:bg-white/80">
                <div className="text-sm font-medium text-sky-600 self-center">Education</div>
                <div className="flex flex-col">
                  <div className="text-slate-700">Master of IT and Master of IT Management</div>
                  <div className="text-slate-700 text-sm mt-1">University of Sydney</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Decorative Elements */}
      <div className="absolute -bottom-32 right-0 w-96 h-96 bg-gradient-to-br from-sky-100/40 to-slate-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob" />
      <div className="absolute top-32 -left-32 w-96 h-96 bg-gradient-to-br from-slate-100/40 to-sky-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000" />
    </section>
  );
};

export default AboutSection;